import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AuthUserDto, LoginRequest } from '@signalvote/shared';
import { authService } from '../services/authService';
import { errorLoggingService } from '../services/errorLoggingService';

export interface AuthContextType {
  user: AuthUserDto | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  logout: () => void;
  refreshUser: () => void;
  setAuthenticatedUser: (user: AuthUserDto) => void;
  loginError: string | null;
  loginLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUserDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);

  useEffect(() => {
    // Check if user is already logged in
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
      errorLoggingService.setUserId(currentUser.id);
    }
    setIsLoading(false);
  }, []);

  const login = useCallback(async (credentials: LoginRequest) => {
    setLoginLoading(true);
    setLoginError(null);

    try {
      const loggedInUser = await authService.login(credentials);
      setUser(loggedInUser);
      errorLoggingService.setUserId(loggedInUser.id);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Login failed';
      setLoginError(errorMessage);
      throw error;
    } finally {
      setLoginLoading(false);
    }
  }, []);

  const logout = useCallback(() => {
    authService.logout();
    setUser(null);
    errorLoggingService.setUserId(undefined);
  }, []);

  const refreshUser = useCallback(() => {
    const currentUser = authService.getCurrentUser();
    if (currentUser) {
      setUser(currentUser);
    }
  }, []);

  const setAuthenticatedUser = useCallback((userData: AuthUserDto) => {
    setUser(userData);
    errorLoggingService.setUserId(userData.id);
  }, []);

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: user !== null,
    login,
    logout,
    refreshUser,
    setAuthenticatedUser,
    loginError,
    loginLoading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { AuthContext };
